<template>
  <BottomModal
    :visible="visible"
    @close="closeModal"
    :fullHeight="false"
  >
    <template #default>
      <div class="change-password-modal__dialog p-3">
        <div class="change-password-modal__header">
          <h5 class="change-password-modal__title">Alterar Senha</h5>
        </div>

        <form @submit.prevent="handleSubmit" class="change-password-modal__form">
          <div class="mb-3">
            <label class="field-label">Senha Atual</label>
            <input
              v-model.trim="form.currentPassword"
              type="password"
              class="form-control register-input"
              placeholder="Digite sua senha atual"
              autocomplete="current-password"
            />
            <button type="button" class="btn btn-link text-link p-0 mt-2" @click="showForgotPasswordFlow = true">
              Esqueci minha senha
            </button>
          </div>

          <div class="mb-3">
            <label class="field-label">Nova Senha</label>
            <input
              v-model.trim="form.newPassword"
              type="password"
              class="form-control register-input"
              placeholder="Digite a nova senha"
              autocomplete="new-password"
            />
          </div>

          <div class="mb-3">
            <label class="field-label">Confirmar Nova Senha</label>
            <input
              v-model.trim="form.confirmPassword"
              type="password"
              class="form-control register-input"
              placeholder="Repita a nova senha"
              autocomplete="new-password"
            />
            <div v-if="form.confirmPassword && !passwordsMatch" class="field-error">
              As senhas não coincidem.
            </div>
          </div>

          <div class="change-password-modal__actions d-flex justify-content-center gap-3 mt-4">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="closeModal" :disabled="loading">
              <i class="fa-solid fa-chevron-down"></i>
            </button>
            <button type="submit" class="btn btn-sm btn-color-site" :disabled="loading">
              <span v-if="loading" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              {{ loading ? 'Salvando...' : 'Salvar nova senha' }}
            </button>
          </div>
        </form>
      </div>
    </template>
  </BottomModal>

  <ForgotPasswordFlow
    v-if="showForgotPasswordFlow"
    mode="bottomSheet"
    :isAuthenticated="true"
    :onSuccess="handleForgotPasswordSuccess"
    :onCancel="handleForgotPasswordCancel"
  />
</template>

<script setup>
import { computed, reactive, watch } from 'vue';
import { toast } from 'vue3-toastify';
import api from '@/services/api';
import BottomModal from '@/components/BottomModal.vue';
import ForgotPasswordFlow from '@/components/ForgotPasswordFlow.vue';

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close', 'success']);

const form = reactive({
  currentPassword: '',
  newPassword: '',
  confirmPassword: '',
});

const state = reactive({
  loading: false,
  showForgotPasswordFlow: false,
});

const loading = computed(() => state.loading);
const showForgotPasswordFlow = computed({
  get: () => state.showForgotPasswordFlow,
  set: (value) => {
    state.showForgotPasswordFlow = value;
  },
});

const passwordsMatch = computed(() => form.newPassword === form.confirmPassword);
const allFilled = computed(() => !!form.currentPassword && !!form.newPassword && !!form.confirmPassword);

function resetForm() {
  form.currentPassword = '';
  form.newPassword = '';
  form.confirmPassword = '';
}

function closeModal() {
  if (state.loading) return;
  emit('close');
}

function handleForgotPasswordSuccess() {
  state.showForgotPasswordFlow = false;
  resetForm();
  emit('close');
}

function handleForgotPasswordCancel() {
  state.showForgotPasswordFlow = false;
}

async function handleSubmit() {
  if (state.loading) return;

  if (!allFilled.value) {
    if (!form.currentPassword) {
      toast.error('Informe a senha atual.');
      return;
    }
    if (!form.newPassword) {
      toast.error('Informe a nova senha.');
      return;
    }
    if (!form.confirmPassword) {
      toast.error('Confirme a nova senha.');
      return;
    }
  }

  if (!passwordsMatch.value) {
    toast.error('A confirmação da nova senha não confere.');
    return;
  }

  state.loading = true;
  try {
    await api.post('/users/change-password', {
      current_password: form.currentPassword,
      new_password: form.newPassword,
      confirm_password: form.confirmPassword,
    });

    toast.success('Senha alterada com sucesso!');
    resetForm();
    emit('success');
    emit('close');
  } catch (error) {
    const message = error?.response?.data?.message || 'Não foi possível alterar sua senha.';
    toast.error(message);
  } finally {
    state.loading = false;
  }
}

watch(
  () => props.visible,
  (value) => {
    if (!value) resetForm();
  }
);
</script>

<style scoped>
.change-password-modal__dialog {
  width: 100%;
  max-width: 520px;
  margin: 0 auto;
}

.change-password-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
}

.change-password-modal__title {
  margin: 0;
  color: #32355d;
  font-weight: 700;
}

.change-password-modal__close {
  border: none;
  background: transparent;
  font-size: 1.4rem;
  line-height: 1;
  color: #9ca3af;
}

.change-password-modal__actions {
  margin-top: 4px;
}

.confirmation-content h5 { font-size: 1.25rem; font-weight: 600; }
.confirmation-content p  { font-size: 0.95rem; }

.btn-color-site {
  background-color: #ff50a3;
  color: #fff;
  border: 1px solid #ff50a3;
  padding: 0.85rem 1.5rem;
  font-size: 1.1rem;
  font-weight: bold;
  border-radius: 0.5rem;
  transition: all 0.2s ease-in-out;
}

.btn-color-site:hover:not(:disabled) {
  background-color: #e64593;
  border-color: #e64593;
  color: #fff;
}

.btn-color-site:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-outline-secondary {
  background-color: transparent;
  color: #6c757d;
  border: 1px solid #ced4da;
  padding: 0.85rem 1.5rem;
  font-size: 1.1rem;
  font-weight: bold;
  border-radius: 0.5rem;
  transition: all 0.2s ease-in-out;
}

.btn-outline-secondary:hover {
  background-color: #f8f9fa;
  border-color: #adb5bd;
  color: #495057;
}

.spinner-border {
  vertical-align: -0.125em;
}

.field-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #32355d;
  margin-bottom: 0.3rem;
  letter-spacing: 0.02em;
  text-transform: uppercase;
}

.field-error {
  font-size: 0.78rem;
  color: #e0185d;
  margin-top: 0.25rem;
}

.register-input {
  background-color: #fafafa;
  border: 1.5px solid #ebebeb;
  border-radius: 0.75rem;
  padding: 0.7rem 1rem;
  font-size: 0.95rem;
  color: #32355d;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.register-input:focus {
  background-color: #fff;
  border-color: #ff50a3;
  box-shadow: 0 0 0 3px rgba(255, 80, 163, 0.12);
  outline: none;
}

.register-input::placeholder {
  color: #c0c0c0;
  font-size: 0.88rem;
}

</style>
